/* CSV -> contact rows, parsed in the BROWSER (every API that takes an upload takes plain JSON
   rows, never a file).

   Address-book and HR exports are messy: Excel writes ';' across most of Europe, Outlook quotes
   any field containing a comma, and everyone names the columns differently. So: sniff the
   delimiter, honour quotes, read by header when there is one, and otherwise fall back to "the
   cell with an @ in it is the email".

   Shared by My Network (a member's own address book) and the company roster (an admin's list of
   colleagues). The two differ only in whether they care about the tags column. */

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CSV_DELIMITERS = [',', ';', '\t'];
const HEADER_EMAIL = /e-?mail/i;
const HEADER_FIRST = /(first|given).*name|voornaam|pr[ée]nom/i;
const HEADER_LAST = /(last|family|sur).*name|achternaam|^naam$|^nom$|^name$/i;
const HEADER_TAGS = /^tags?$|^labels?$|^groups?$|^afdeling$|^department$/i;
// The team a person plays in, on the competition round trip: export who is coming, fill this
// column in a spreadsheet, upload it back. Read by NAME only and never guessed — without a
// header there is no telling a team from a job title, and guessing would file people against a
// team they are not in.
const HEADER_TEAM = /^team$|^ploeg$|^[ée]quipe$/i;
// The standing house a colleague belongs to, on the roster round trip: export the roster, fill
// this column in, upload it back. Read by NAME only, for the same reason as the team above — a
// column of words with no header could be a department, a site or a job title, and guessing files
// people into houses they were never meant to be in.
const HEADER_HOUSE = /^house$|^huis$|^maison$/i;

// Inside one cell, tags are separated by something that is NOT a CSV delimiter — otherwise the
// split would have happened before we ever saw the cell. A pipe or a slash is what people reach
// for; a comma works too, but only because a multi-tag cell is quoted when it contains one.
const TAG_SEPARATORS = /[|/,]/;

export const splitCsvLine = (line, delim) => {
  const out = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (quoted) {
      if (ch !== '"') cur += ch;
      else if (line[i + 1] === '"') { cur += '"'; i += 1; }   // "" = a literal quote
      else quoted = false;
    } else if (ch === '"') quoted = true;
    else if (ch === delim) { out.push(cur.trim()); cur = ''; }
    else cur += ch;
  }
  out.push(cur.trim());
  return out;
};

export const parseTags = (cell) => (cell || '')
  .split(TAG_SEPARATORS)
  .map((s) => s.trim())
  .filter(Boolean);

/* Returns [{email, first_name, last_name, tags, team, house}] — rows without a valid address are
   dropped. ``team`` and ``house`` are '' unless the file names such a column; callers that do not
   care about them simply ignore them. */
export const parseContactsCsv = (text) => {
  const lines = String(text || '').replace(/^﻿/, '')   // Excel writes a BOM
    .split(/\r\n|\n|\r/).filter((l) => l.trim());
  if (!lines.length) return [];
  const delim = CSV_DELIMITERS.reduce(
    (best, d) => (lines[0].split(d).length > lines[0].split(best).length ? d : best),
    CSV_DELIMITERS[0]);
  const rows = lines.map((l) => splitCsvLine(l, delim));

  // A header row is one that NAMES an email column without containing an address itself —
  // then the columns are read by name, so any export order works.
  const head = rows[0];
  const hasHeader = head.some((c) => HEADER_EMAIL.test(c)) && !head.some((c) => c.includes('@'));
  const cols = hasHeader ? {
    email: head.findIndex((c) => HEADER_EMAIL.test(c)),
    first: head.findIndex((c) => HEADER_FIRST.test(c)),
    last: head.findIndex((c) => HEADER_LAST.test(c)),
    tags: head.findIndex((c) => HEADER_TAGS.test(c)),
    team: head.findIndex((c) => HEADER_TEAM.test(c)),
    house: head.findIndex((c) => HEADER_HOUSE.test(c)),
  } : null;

  const contacts = [];
  (hasHeader ? rows.slice(1) : rows).forEach((cells) => {
    let email;
    let first = '';
    let last = '';
    let tags = [];
    let team = '';
    let house = '';
    if (cols) {
      email = cells[cols.email];
      if (cols.first >= 0) first = cells[cols.first] || '';
      if (cols.last >= 0) last = cells[cols.last] || '';
      // Tags, the team and the house only ever arrive by name: without a header there is no way
      // to tell such a column from a job title or a phone number, and guessing would file junk
      // against people.
      if (cols.tags >= 0) tags = parseTags(cells[cols.tags]);
      if (cols.team >= 0) team = (cells[cols.team] || '').trim();
      if (cols.house >= 0) house = (cells[cols.house] || '').trim();
    } else {
      const at = cells.findIndex((c) => c && c.includes('@'));
      if (at < 0) return;
      email = cells[at];
      // The documented shape is "name, firstname, email", so what's left reads last-name first.
      const rest = cells.filter((_, i) => i !== at).filter(Boolean);
      if (rest.length > 1) { [last, first] = rest; } else { [first] = rest; }
    }
    email = (email || '').trim();
    if (!EMAIL_PATTERN.test(email)) return;
    contacts.push({
      email,
      first_name: (first || '').trim(),
      last_name: (last || '').trim(),
      tags,
      team,
      house,
    });
  });
  return contacts;
};

/* ------------------------------------------------------------------ writing (export side)

   The company roster is a round trip: the admin exports what they are looking at, edits the
   tags in their own spreadsheet, and uploads the same file again. So the writer here must
   produce something `parseContactsCsv` reads back exactly — same column names, and tags joined
   by a character that is NOT a CSV delimiter.

   ';' rather than ',' because these files are opened in Excel in Belgium and the Gulf, where a
   comma-separated file lands in one column; the parser above sniffs the delimiter, so both come
   back in either way. A BOM for the same reason — without it Excel reads UTF-8 as latin-1 and a
   name with an accent comes back broken. */

const CSV_DELIM = ';';
const CSV_BOM = '\ufeff';

// Excel, LibreOffice and Sheets execute a cell that STARTS with one of these — and the names in
// this export were typed by other people. Same guard, and same apostrophe fix, as
// shared_models/services/spreadsheet.py applies on the server's own exports.
const FORMULA_PREFIXES = ['=', '+', '-', '@'];

export const spreadsheetSafe = (value) => {
  const text = value == null ? '' : String(value);
  const stripped = text.replace(/^[\t\r\n]+/, '');
  if (!stripped || !FORMULA_PREFIXES.includes(stripped[0])) return text;
  // A plain number keeps its meaning: '-5' is minus five, not a formula.
  return Number.isNaN(Number(stripped)) ? `'${text}` : text;
};

export const csvCell = (value) => {
  const text = spreadsheetSafe(value);
  return /["\n\r;,\t]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/* rows = array of arrays. Returns the whole file as one string (BOM included). */
export const buildCsv = (rows) => CSV_BOM
  + (rows || []).map((cells) => (cells || []).map(csvCell).join(CSV_DELIM)).join('\r\n');

/* Hand the browser a file. Same gesture as MemberBadge's download — an <a download> on a blob,
   revoked straight after, so nothing is left holding the bytes. */
export const downloadCsv = (fileName, text) => {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/* The column names the parser reads by name, and the separator for a multi-tag cell.

   ★ 'House' is on the END, and it is the same file. A company sorts its people into houses by
   editing the roster it already exports, not by producing a second spreadsheet that has to agree
   with the first about who exists. A company that uses no houses simply leaves the column empty
   and never notices it — which is why it costs nothing to have it here for everybody. */
export const CONTACT_COLUMNS = ['First name', 'Last name', 'Email', 'Tags', 'House'];
export const TAG_JOINER = '|';

/* The team round trip's columns. Kept here beside the parser that reads them and matching what
   ``routes/company_teams.get_company_event_teams_csv`` writes on the server: a template whose
   headers drift from either one produces a file that uploads and seats nobody. */
export const TEAM_COLUMNS = ['First name', 'Last name', 'Email', 'Team'];

export const contactRow = (c) => [
  c.first_name || '', c.last_name || '', c.email || '',
  (c.tags || []).join(TAG_JOINER),
  c.house || '',
];
